import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatINR, todayStr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Loader2, Wifi, Tv, Users, Truck, Edit3 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "salary", label: "Salary" },
  { value: "inventory", label: "Inventory" },
  { value: "drawings", label: "Drawings" },
];

const SERVICE_OPTIONS = [
  { value: "internet", label: "Internet" },
  { value: "cable", label: "Cable TV" },
  { value: "both", label: "Both" },
];

function PartyDialog({ partyType, existing, open, onOpenChange, onDone }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({ name: "", phone: "", service_type: "both", note: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name || "",
        phone: existing.phone || "",
        service_type: existing.service_type || "both",
        note: existing.note || "",
      });
    } else {
      setForm({ name: "", phone: "", service_type: "both", note: "" });
    }
  }, [existing, open]);

  const submit = async () => {
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/expense-parties/${existing.id}`, form);
        toast.success("Saved");
      } else {
        await api.post("/expense-parties", { ...form, party_type: partyType });
        toast.success("Added");
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const noun = partyType === "employee" ? "Employee" : "Supplier";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${partyType}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${noun}` : `Add ${noun}`}</DialogTitle>
          <DialogDescription>
            {partyType === "employee"
              ? "Employees are picked when recording a Salary expense."
              : "Suppliers are picked when recording an Inventory expense."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input data-testid={`${partyType}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input data-testid={`${partyType}-phone-input`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label>Service</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
              <SelectTrigger data-testid={`${partyType}-service-select`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea data-testid={`${partyType}-note-input`} rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={`${partyType}-save-btn`} disabled={loading || !form.name.trim()} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save" : "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartyManager({ partyType, refreshTrigger, onChanged }) {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expense-parties", { params: { party_type: partyType } });
      setParties(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [partyType, refreshTrigger]);

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${partyType}?`)) return;
    try {
      await api.delete(`/expense-parties/${id}`);
      toast.success("Deleted");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const noun = partyType === "employee" ? "Employee" : "Supplier";
  const Icon = partyType === "employee" ? Users : Truck;

  return (
    <>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-gray-500" />
            <h2 className="font-display font-semibold text-lg">{noun}s</h2>
          </div>
          <Button
            data-testid={`add-${partyType}-btn`}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-red-600 hover:bg-red-700"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" /> Add {noun}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid={`${partyType}-table`}>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
              )}
              {!loading && parties.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No {noun.toLowerCase()}s yet.</TableCell></TableRow>
              )}
              {parties.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.phone || "—"}</TableCell>
                  <TableCell className="capitalize">{p.service_type === "cable" ? "Cable TV" : p.service_type}</TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-[240px] truncate" title={p.note}>{p.note || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button data-testid={`edit-${partyType}-${p.id}`} size="icon" variant="ghost" onClick={() => { setEditing(p); setDialogOpen(true); }}>
                        <Edit3 className="h-4 w-4 text-gray-600" />
                      </Button>
                      <Button data-testid={`delete-${partyType}-${p.id}`} size="icon" variant="ghost" onClick={() => remove(p.id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      <PartyDialog
        partyType={partyType}
        existing={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onDone={() => { load(); onChanged?.(); }}
      />
    </>
  );
}

function AddExpenseDialog({ onCreated, partyRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    service_type: "internet",
    category: "salary",
    amount: "",
    expense_date: todayStr(),
    note: "",
    party_id: "",
  });
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);

  const partyTypeForCategory = form.category === "salary" ? "employee" : form.category === "inventory" ? "supplier" : null;

  // Reload party list whenever category changes
  useEffect(() => {
    if (!open) return;
    if (!partyTypeForCategory) {
      setParties([]);
      setForm((f) => ({ ...f, party_id: "" }));
      return;
    }
    api.get("/expense-parties", { params: { party_type: partyTypeForCategory } })
      .then((r) => setParties(r.data))
      .catch(() => setParties([]));
    // reset party selection on category change
    setForm((f) => ({ ...f, party_id: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category, open, partyRefresh]);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!partyTypeForCategory) delete payload.party_id;
      await api.post("/expenses", payload);
      toast.success("Expense added");
      setOpen(false);
      setForm({ service_type: "internet", category: "salary", amount: "", expense_date: todayStr(), note: "", party_id: "" });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const partyRequired = !!partyTypeForCategory;
  const canSave = !!form.amount && (!partyRequired || !!form.party_id);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-expense-btn" className="bg-red-600 hover:bg-red-700"><Plus className="h-4 w-4 mr-1" /> Add Transaction</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="add-expense-dialog">
        <DialogHeader>
          <DialogTitle>Add Expense Transaction</DialogTitle>
          <DialogDescription>Salary → pick Employee · Inventory → pick Supplier · Drawings → no party.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Service</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
              <SelectTrigger data-testid="expense-service-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="internet">Internet</SelectItem>
                <SelectItem value="cable">Cable TV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {partyRequired && (
            <div className="col-span-2">
              <Label>{partyTypeForCategory === "employee" ? "Employee" : "Supplier"}</Label>
              <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                <SelectTrigger data-testid="expense-party-select"><SelectValue placeholder={`Select ${partyTypeForCategory}…`} /></SelectTrigger>
                <SelectContent>
                  {parties.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No {partyTypeForCategory}s yet. Add one from the {partyTypeForCategory === "employee" ? "Employees" : "Suppliers"} tab.
                    </div>
                  )}
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.phone ? ` · ${p.phone}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Amount (₹)</Label>
            <Input data-testid="expense-amount-input" type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <Label>Date</Label>
            <Input data-testid="expense-date-input" type="date" max={todayStr()} value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Note (optional)</Label>
            <Textarea data-testid="expense-note-input" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="expense-save-btn" disabled={loading || !canSave} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransactionsView({ refreshKey, onChange, partyRefresh }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ service_type: "all", category: "all", date_from: "", date_to: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.service_type !== "all") params.service_type = filters.service_type;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/expenses", { params });
      setExpenses(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, refreshKey]);

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const totals = expenses.reduce(
    (acc, e) => {
      acc.total += e.amount;
      acc[e.service_type] = (acc[e.service_type] || 0) + e.amount;
      acc.byCat[e.category] = (acc.byCat[e.category] || 0) + e.amount;
      return acc;
    },
    { total: 0, internet: 0, cable: 0, byCat: { salary: 0, inventory: 0, drawings: 0 } },
  );

  return (
    <>
      <div className="flex items-center justify-end">
        <AddExpenseDialog onCreated={load} partyRefresh={partyRefresh} />
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label>Service</Label>
            <Select value={filters.service_type} onValueChange={(v) => setFilters({ ...filters, service_type: v })}>
              <SelectTrigger data-testid="expense-filter-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="internet">Internet</SelectItem>
                <SelectItem value="cable">Cable TV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger data-testid="expense-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input data-testid="expense-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div>
            <Label>To</Label>
            <Input data-testid="expense-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Total</div><div className="pds-stat-num text-2xl mt-1">{formatINR(totals.total)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500 flex items-center gap-1"><Wifi className="h-3 w-3" /> Internet</div><div className="pds-stat-num text-2xl mt-1">{formatINR(totals.internet)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500 flex items-center gap-1"><Tv className="h-3 w-3" /> Cable</div><div className="pds-stat-num text-2xl mt-1">{formatINR(totals.cable)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Salary</div><div className="pds-stat-num text-xl mt-1">{formatINR(totals.byCat.salary || 0)}</div></Card>
        <Card className="p-4"><div className="text-xs uppercase text-gray-500">Inventory + Drawings</div><div className="pds-stat-num text-xl mt-1">{formatINR((totals.byCat.inventory || 0) + (totals.byCat.drawings || 0))}</div></Card>
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="expenses-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Added By</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
              )}
              {!loading && expenses.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-6">No expenses recorded.</TableCell></TableRow>
              )}
              {expenses.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-sm">{e.expense_date}</TableCell>
                  <TableCell><span className="status-chip status-active">{e.service_type === "cable" ? "Cable TV" : "Internet"}</span></TableCell>
                  <TableCell className="capitalize">{e.category}</TableCell>
                  <TableCell>
                    {e.party_name ? (
                      <div>
                        <div className="text-sm font-medium">{e.party_name}</div>
                        <div className="text-xs text-gray-500 capitalize">{e.party_type}</div>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell className="text-sm text-gray-700 max-w-[240px] truncate" title={e.note}>{e.note || "—"}</TableCell>
                  <TableCell className="text-sm">{e.created_by_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{formatINR(e.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`delete-expense-${e.id}`} size="icon" variant="ghost" onClick={() => remove(e.id)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
}

export default function ExpensesPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [partyRefresh, setPartyRefresh] = useState(0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Expenses</h1>
        <p className="text-gray-500 mt-1 text-sm">Track Salary, Inventory & Drawings — separate for Internet & Cable TV.</p>
      </div>

      <Tabs defaultValue="transactions">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="transactions" data-testid="exp-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="employees" data-testid="exp-tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="exp-tab-suppliers">Suppliers</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-4 space-y-4">
          <TransactionsView refreshKey={refreshKey} onChange={() => setRefreshKey((k) => k + 1)} partyRefresh={partyRefresh} />
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <PartyManager partyType="employee" refreshTrigger={partyRefresh} onChanged={() => setPartyRefresh((k) => k + 1)} />
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4">
          <PartyManager partyType="supplier" refreshTrigger={partyRefresh} onChanged={() => setPartyRefresh((k) => k + 1)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
